import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Gauge, FileText, MapPin } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import FAQAccordion from "@/components/FAQAccordion";
import InterestQuiz from "@/components/InterestQuiz";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import { websiteSchema, faqPageSchema, itemListSchema, SITE_URL } from "@/lib/schema";
import { CITIES, STENO_FAQ, LEGAL_FAQ, BLOG_POSTS } from "@/data/content";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Home() {
  useSEO({
    title:
      "LexWorkspace — Stenography Training & AI Legal Drafting | DC · NYC · LA · Chicago · Houston",
    description:
      "LexWorkspace is the hub for two best-in-class platforms: StenoPaceMaster (stenography speed building & court-reporting certification practice) and DraftMaster.ai (AI legal drafting & contract automation). Serving Washington DC, New York City, Los Angeles, Chicago, and Houston.",
    keywords:
      "stenography training online, stenography speed building program, AI legal drafting software, automated contract drafting tool, court reporting speed practice, AI tool for lawyers, legal document automation",
    url: SITE_URL + "/",
  });

  return (
    <div data-testid="home-page">
      <JsonLd id="home-website" data={websiteSchema()} />
      <JsonLd
        id="home-faq"
        data={faqPageSchema([...STENO_FAQ.slice(0, 3), ...LEGAL_FAQ.slice(0, 3)])}
      />
      <JsonLd
        id="home-itemlist"
        data={itemListSchema(
          [
            {
              name: "StenoPaceMaster — Stenography Training",
              url: SITE_URL + "/stenopacemaster",
            },
            {
              name: "DraftMaster.ai — AI Legal Drafting",
              url: SITE_URL + "/draftmaster",
            },
          ],
          "LexWorkspace Platforms"
        )}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-32 lg:pt-32 lg:pb-40">
          <motion.p
            {...fadeUp}
            className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6"
          >
            Two crafts. One hub. Five cities.
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="home-h1"
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl"
          >
            Speed for stenographers.{" "}
            <span className="text-[#1E3A8A]">Drafts</span> for attorneys.
            <br />
            <span className="text-[#065F46]">Built for both.</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 max-w-2xl text-lg text-gray-600 leading-relaxed"
          >
            LexWorkspace promotes two best-in-class platforms: StenoPaceMaster
            for online stenography speed-building, certification practice and
            realtime drills — and DraftMaster.ai for AI-powered legal drafting,
            contract automation and litigation document creation. Serving
            professionals nationwide, with dedicated guides for Washington DC,
            New York City, Los Angeles, Chicago, and Houston.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/stenopacemaster"
              data-testid="hero-cta-steno"
              className="btn-sharp bg-[#1E3A8A] text-white hover:bg-[#1D4ED8] inline-flex items-center gap-2"
            >
              Explore Stenography <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/draftmaster"
              data-testid="hero-cta-draft"
              className="btn-sharp bg-[#065F46] text-white hover:bg-[#047857] inline-flex items-center gap-2"
            >
              Explore Legal AI <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* DUAL PATH BENTO */}
      <section className="border-t border-black/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 border-r border-l border-black/10">
          {/* STENO */}
          <Link
            to="/stenopacemaster"
            data-testid="dual-path-steno"
            className="group relative p-10 lg:p-16 border-b lg:border-b-0 lg:border-r border-black/10 hover:bg-[#EFF6FF] transition-colors"
          >
            <Gauge size={32} className="text-[#1E3A8A] mb-8" strokeWidth={1.5} />
            <p className="text-xs tracking-[0.25em] uppercase text-[#1E3A8A] font-semibold mb-3">
              StenoPaceMaster
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none mb-4">
              Stenography speed building, online.
            </h2>
            <p className="text-gray-600 mb-8 max-w-md leading-relaxed">
              Daily dictation, RPR/RMR/CRR certification prep, realtime drills
              for CART providers and broadcast captioners.
            </p>
            <span className="inline-flex items-center gap-2 font-semibold text-sm tracking-wide uppercase text-[#1E3A8A] group-hover:gap-3 transition-all">
              See the program <ArrowUpRight size={14} />
            </span>
          </Link>

          {/* DRAFTMASTER */}
          <Link
            to="/draftmaster"
            data-testid="dual-path-draft"
            className="group relative p-10 lg:p-16 hover:bg-[#ECFDF5] transition-colors"
          >
            <FileText size={32} className="text-[#065F46] mb-8" strokeWidth={1.5} />
            <p className="text-xs tracking-[0.25em] uppercase text-[#065F46] font-semibold mb-3">
              DraftMaster.ai
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none mb-4">
              AI legal drafting for serious firms.
            </h2>
            <p className="text-gray-600 mb-8 max-w-md leading-relaxed">
              Contracts, motions, briefs, real-estate and employment
              documents — drafted, reviewed, and audited for accuracy.
            </p>
            <span className="inline-flex items-center gap-2 font-semibold text-sm tracking-wide uppercase text-[#065F46] group-hover:gap-3 transition-all">
              See the platform <ArrowUpRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* INTEREST QUIZ — converts before they scroll */}
      <InterestQuiz />

      {/* CITY GRID */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
                Where we focus
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none max-w-2xl">
                Five cities. Two crafts. Local context that actually matters.
              </h2>
            </div>
            <p className="text-gray-600 max-w-md text-base leading-relaxed">
              Court reporting venues, captioning markets, federal & state
              filings, deposition volumes, bar exam cadence — local context
              drives both stenography and legal AI adoption.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-black/10">
            {CITIES.map((c, idx) => (
              <Link
                key={c.slug}
                to={`/steno/${c.slug}`}
                data-testid={`city-card-${c.slug}`}
                className={
                  "group relative aspect-[3/4] overflow-hidden " +
                  (idx < CITIES.length - 1
                    ? "sm:border-r border-b sm:border-b-0 border-black/10"
                    : "")
                }
              >
                <img
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <MapPin size={16} className="mb-3" />
                  <p className="font-serif-display text-2xl leading-tight">
                    {c.name}
                  </p>
                  <p className="text-xs tracking-widest uppercase mt-2 text-white/70">
                    Steno + Legal AI
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / STATS */}
      <section className="bg-[#111] text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-4 gap-12">
          {[
            { k: "13,800+", v: "Reporters in our 5 focus cities" },
            { k: "28,800+", v: "Law firms in our 5 focus cities" },
            { k: "225 wpm", v: "Avg target speed of subscribers" },
            { k: "60s", v: "Avg first-draft time on DraftMaster" },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <p className="font-serif-display text-5xl mb-3">{s.k}</p>
              <p className="text-sm text-white/60 leading-relaxed">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG TEASER */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12 gap-6">
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              From the archive.
            </h2>
            <Link
              to="/blog"
              data-testid="home-blog-all"
              className="text-xs tracking-[0.25em] uppercase font-semibold hover:underline"
            >
              All Insights →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {BLOG_POSTS.slice(0, 3).map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`home-blog-${p.slug}`}
                className="group border border-black/10 bg-white hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <p
                    className={
                      "text-xs tracking-[0.2em] uppercase font-semibold mb-3 " +
                      (p.product === "steno"
                        ? "text-[#1E3A8A]"
                        : "text-[#065F46]")
                    }
                  >
                    {p.product === "steno" ? "Stenography" : "Legal AI"}
                  </p>
                  <h3 className="font-serif-display text-2xl leading-tight mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMBINED FAQ */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Frequently asked
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Answers, not marketing.
            </h2>
            <p className="text-gray-600 mt-6 max-w-sm leading-relaxed">
              Quick answers about both platforms. For anything else, the
              floating chat assistant goes deeper.
            </p>
          </div>
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1E3A8A] mb-2">
              Stenography
            </p>
            <FAQAccordion items={STENO_FAQ.slice(0, 3)} testIdPrefix="home-steno-faq" />
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#065F46] mt-12 mb-2">
              Legal AI
            </p>
            <FAQAccordion items={LEGAL_FAQ.slice(0, 3)} testIdPrefix="home-legal-faq" />
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section className="py-24 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Get on the list
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none">
              One short email a month.{" "}
              <span className="text-gray-400">No spam.</span>
            </h2>
            <p className="text-gray-600 mt-6 max-w-md leading-relaxed">
              Practical tips on stenography speed building and AI legal
              drafting — written for working professionals, not search engines.
            </p>
          </div>
          <div>
            <LeadForm
              interest="both"
              source="home-footer-cta"
              testIdPrefix="home-lead"
              ctaLabel="Subscribe"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
